import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { IconButton } from '@velove/design-system';
import { useUpdateDeliveryType } from './hooks/useUpdateDeliveryType';
import { DeliveryMethod, TaskType } from '../../contexts/contextTypes';
import { useParams } from 'react-router-dom';

interface PickupPoint {
  provider: string;
  type: string;
  id: string;
  address: {
    streetName: string;
    zipCode: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
    description: string | null;
  };
}

interface DeliveryTypeChangerProps {
  taskId: string;
  taskType: TaskType;
  pickupPoints: PickupPoint[];
  currentPickupPointId?: string;
  canChangeDeliveryType: boolean;
}

const DeliveryTypeChanger = ({ taskId, taskType, pickupPoints, currentPickupPointId, canChangeDeliveryType }: DeliveryTypeChangerProps) => {
  const { t } = useTranslation();
  const { packageId } = useParams();

  const getInitialDeliveryMethod = (): DeliveryMethod | null => {
    if (taskType === TaskType.DELIVERY) return DeliveryMethod.HOME_DELIVERY;
    if (taskType === TaskType.LOCKER_DELIVERY) return DeliveryMethod.BOX_DELIVERY;
    return null;
  };

  const initialDeliveryMethod = getInitialDeliveryMethod();

  const [selectedDeliveryType, setSelectedDeliveryType] = useState<DeliveryMethod>(initialDeliveryMethod ?? DeliveryMethod.HOME_DELIVERY);
  const [isEditingDeliveryType, setIsEditingDeliveryType] = useState(false);
  const [selectedPickupPoint, setSelectedPickupPoint] = useState<string>(
    initialDeliveryMethod === DeliveryMethod.BOX_DELIVERY && currentPickupPointId ? currentPickupPointId : ''
  );
  const [isDeliveryTypeDropdownOpen, setIsDeliveryTypeDropdownOpen] = useState(false);
  const [isPickupPointDropdownOpen, setIsPickupPointDropdownOpen] = useState(false);
  const [deliveryTypeError, setDeliveryTypeError] = useState<string>('');
  const deliveryTypeDropdownRef = useRef<HTMLDivElement>(null);
  const pickupPointDropdownRef = useRef<HTMLDivElement>(null);

  const updateDeliveryTypeMutation = useUpdateDeliveryType();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (deliveryTypeDropdownRef.current && !deliveryTypeDropdownRef.current.contains(event.target as Node)) {
        setIsDeliveryTypeDropdownOpen(false);
      }
      if (pickupPointDropdownRef.current && !pickupPointDropdownRef.current.contains(event.target as Node)) {
        setIsPickupPointDropdownOpen(false);
      }
    };

    if (isDeliveryTypeDropdownOpen || isPickupPointDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDeliveryTypeDropdownOpen, isPickupPointDropdownOpen]);

  if (!initialDeliveryMethod) {
    return null;
  }

  const handleDeliveryTypeChange = async () => {
    if (!packageId || !taskId) return;

    setDeliveryTypeError('');

    if (selectedDeliveryType === DeliveryMethod.BOX_DELIVERY && !selectedPickupPoint) {
      setDeliveryTypeError('Please select a pickup point.');
      return;
    }

    try {
      await updateDeliveryTypeMutation.mutateAsync({
        packageId,
        payload: {
          taskId,
          deliveryType: selectedDeliveryType,
          ...(selectedDeliveryType === DeliveryMethod.BOX_DELIVERY && { pickupPointId: selectedPickupPoint })
        }
      });

      setIsEditingDeliveryType(false);
      setIsDeliveryTypeDropdownOpen(false);
      setIsPickupPointDropdownOpen(false);
      console.log('Delivery type successfully updated');
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || 'Failed to update delivery type. Please try again.';
      setDeliveryTypeError(errorMsg);
      console.error('Error updating delivery type:', errorMsg);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingDeliveryType(false);
    setSelectedDeliveryType(initialDeliveryMethod);
    setSelectedPickupPoint('');
    setIsDeliveryTypeDropdownOpen(false);
    setIsPickupPointDropdownOpen(false);
    setDeliveryTypeError('');
  };

  return (
    <div className="delivery-type-change-section" style={{ marginBottom: 40, marginTop: 30 }}>
      <p style={{ marginBottom: 10 }}>{t('changeDeliveryMethod')}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: deliveryTypeError ? 10 : 20 }}>
        {!isEditingDeliveryType ? (
          <>
            <h3 id="delivery-type-display" className="eta-display-text" style={{ marginBottom: 0 }}>
              {selectedDeliveryType === DeliveryMethod.HOME_DELIVERY ? t('home_delivery') : t('box_delivery')}
            </h3>
            {canChangeDeliveryType && (
              <IconButton
                id="edit-delivery-type-button"
                icon={'Edit'}
                sx={{
                  height: '40px',
                  width: '40px',
                  padding: '4px',
                  color: 'white',
                  borderRadius: '100%',
                  borderStyle: 'solid',
                  borderWidth: '2px',
                  backgroundColor: 'var(--container-bg-color)',
                  borderColor: 'white'
                }}
                onClick={() => {
                  setIsEditingDeliveryType(true);
                  setDeliveryTypeError('');
                }}
              />
            )}
          </>
        ) : (
          <>
            <div ref={deliveryTypeDropdownRef} className="custom-dropdown-wrapper">
              <div className="custom-dropdown-trigger" onClick={() => setIsDeliveryTypeDropdownOpen(!isDeliveryTypeDropdownOpen)}>
                <span>{selectedDeliveryType === DeliveryMethod.HOME_DELIVERY ? t('home_delivery') : t('box_delivery')}</span>
                <span style={{ marginLeft: '8px' }}>▼</span>
              </div>
              {isDeliveryTypeDropdownOpen && (
                <div className="custom-dropdown-menu">
                  <div
                    className={`custom-dropdown-item ${selectedDeliveryType === DeliveryMethod.HOME_DELIVERY ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedDeliveryType(DeliveryMethod.HOME_DELIVERY);
                      setSelectedPickupPoint('');
                      setIsDeliveryTypeDropdownOpen(false);
                    }}
                  >
                    {t('home_delivery')}
                  </div>
                  <div
                    className={`custom-dropdown-item ${selectedDeliveryType === DeliveryMethod.BOX_DELIVERY ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedDeliveryType(DeliveryMethod.BOX_DELIVERY);
                      setIsDeliveryTypeDropdownOpen(false);
                    }}
                  >
                    {t('box_delivery')}
                  </div>
                </div>
              )}
            </div>

            {selectedDeliveryType === DeliveryMethod.HOME_DELIVERY && (
              <>
                <Button
                  id="confirm-delivery-type-button"
                  className="reschedule-confirm-button"
                  onClick={handleDeliveryTypeChange}
                  disabled={updateDeliveryTypeMutation.isLoading}
                >
                  {updateDeliveryTypeMutation.isLoading ? '⏳' : '✓'}
                </Button>
                <Button id="cancel-delivery-type-button" className="reschedule-cancel-button" onClick={handleCancelEdit}>
                  ✕
                </Button>
              </>
            )}
          </>
        )}
      </div>

      {isEditingDeliveryType && selectedDeliveryType === DeliveryMethod.BOX_DELIVERY && pickupPoints && pickupPoints.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', marginBottom: deliveryTypeError ? 10 : 20 }}>
          <div ref={pickupPointDropdownRef} className="custom-dropdown-wrapper">
            <div className="custom-dropdown-trigger" onClick={() => setIsPickupPointDropdownOpen(!isPickupPointDropdownOpen)}>
              <span>{selectedPickupPoint ? pickupPoints.find((p) => p.id === selectedPickupPoint)?.address?.streetName : t('select_pickup_point')}</span>
              <span style={{ marginLeft: '8px' }}>▼</span>
            </div>
            {isPickupPointDropdownOpen && (
              <div className="custom-dropdown-menu">
                {pickupPoints.map((point) => (
                  <div
                    key={point.id}
                    className={`custom-dropdown-item ${selectedPickupPoint === point.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedPickupPoint(point.id);
                      setIsPickupPointDropdownOpen(false);
                    }}
                  >
                    {point.address.streetName}, {point.address.zipCode} {point.address.city}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button
            id="confirm-delivery-type-button"
            className="reschedule-confirm-button"
            onClick={handleDeliveryTypeChange}
            disabled={!selectedPickupPoint || updateDeliveryTypeMutation.isLoading}
          >
            {updateDeliveryTypeMutation.isLoading ? '⏳' : '✓'}
          </Button>
          <Button id="cancel-delivery-type-button" className="reschedule-cancel-button" onClick={handleCancelEdit}>
            ✕
          </Button>
        </div>
      )}

      {deliveryTypeError && (
        <div
          style={{
            padding: '10px',
            marginBottom: '20px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c33'
          }}
        >
          {deliveryTypeError}
        </div>
      )}
    </div>
  );
};

export default DeliveryTypeChanger;
